import { batch } from 'react-redux';
import { cleanSelectBlock } from 'src/hooks/utils/clean-select';
import { uiActions } from 'src/redux/reducers/ui';
import { cache, dispatch } from 'src/redux/store';
import { isCollection } from 'src/utils/block-type-utils';
import { filterDescendantId, getAllBlockInfoFromDomContent } from 'src/utils/block-utils';
import { handleLastMouseEventInCells } from 'src/utils/cell-utils';
import { getOwnerPage } from '../../block/use-get-owner-page';

/** 处理shift mousedown 鼠标多选block逻辑*/
export const handleShiftMouseDown = (event: MouseEvent, isInRight: boolean) => {
  if (!(event.target instanceof Element)) {
    return;
  }

  let startBlockId = cache.ui.selectedCells[0]?.recordId;
  let startBlockSyncId = cache.ui.selectedCells[0]?.syncId;
  const activeBlockElement = document.activeElement?.closest('[data-block-id');
  if (activeBlockElement instanceof HTMLElement) {
    startBlockId = activeBlockElement.dataset.blockId;
    startBlockSyncId = activeBlockElement.dataset.syncId;
  }

  /**
   * 有2种情况
   * 1.当前有选中的单元格，这时候如果shift+点击到同一个表格的其它单元格的话，就还是多选单元格
   * 2.其它情况就让它转成多选block
   */
  if (cache.ui.selectedCells.length) {
    const cellElement = event.target.closest('[data-property-id]');
    if (cellElement instanceof HTMLElement) {
      // 如果此时是选中了单元格的情况下按shift+点击，则看看是不是点击了其它单元的，如果是的话就那是多选单元格
      handleLastMouseEventInCells(event);
      return;
    }
  }

  const lastSelectedNode = event.target.closest('[data-block-id');
  if (!(lastSelectedNode instanceof HTMLElement)) {
    // 点击到外面就reset
    cleanSelectBlock();
    return;
  }

  const lastSelectedBlockId = lastSelectedNode.dataset.blockId;
  const lastSyncId = lastSelectedNode.dataset.syncId;
  const { viewId, syncId } = lastSelectedNode.dataset;

  if (!lastSelectedBlockId) {
    return;
  }

  const blockInfo = getAllBlockInfoFromDomContent(isInRight);
  if (!blockInfo) {
    return;
  }

  // 多选block支持取消，跟文件夹/excel shift多选的行为一致，notion不支持取消，只能无限添加
  let focusedBlockId: string | undefined = undefined;
  let focusedBlockSyncId: string | undefined = undefined;
  if (cache.ui.selectedBlocks[0]?.blockId) {
    const focusIndex = cache.ui.isSelectTopToBottom ? 0 : cache.ui.selectedBlocks.length - 1;
    focusedBlockId = cache.ui.selectedBlocks[focusIndex]?.blockId;
    focusedBlockSyncId = cache.ui.selectedBlocks[focusIndex]?.syncId;
  } else if (startBlockId) {
    focusedBlockId = startBlockId;
    focusedBlockSyncId = startBlockSyncId;
  } else if (lastSelectedBlockId) {
    focusedBlockId = lastSelectedBlockId;
    focusedBlockSyncId = lastSyncId;
  }

  const focusedBlockIndex = blockInfo.findIndex(
    (v) => v.blockId === focusedBlockId && v.viewId === viewId && v.syncId === focusedBlockSyncId
  );
  const lastSelectedBlockIndex = blockInfo.findIndex(
    (v) => v.blockId === lastSelectedBlockId && v.viewId === viewId && v.syncId === syncId
  );
  const isTopToBottom = focusedBlockIndex < lastSelectedBlockIndex;

  const startIndex = isTopToBottom ? focusedBlockIndex : lastSelectedBlockIndex;
  const endIndex = isTopToBottom ? lastSelectedBlockIndex : focusedBlockIndex;
  let newSelectedBlock = blockInfo.slice(startIndex, endIndex + 1);
  if (newSelectedBlock.length === 1) {
    // 如果是在富文本单行的情况下，不要选中block，因为可能是多选文本进行操作的场景
    const ownerId = getOwnerPage(newSelectedBlock[0]?.blockId ?? '');
    if (ownerId && !isCollection(cache.blocks[ownerId]?.type)) {
      return;
    }
  }
  event.stopImmediatePropagation();
  setTimeout(() => {
    // 如果是选中了文本就不要选中block
    if (newSelectedBlock.length === 0 && !window.getSelection().isCollapsed) {
      return;
    }
    newSelectedBlock = filterDescendantId(newSelectedBlock);
    batch(() => {
      dispatch(uiActions.updateSelectBlocks(newSelectedBlock));
      dispatch(
        uiActions.update({
          isSelectTopToBottom: isTopToBottom,
        })
      );
    });
    window.getSelection().removeAllRanges();
    document.dispatchEvent(new Event('_clearTextSelection'));
    (document.activeElement as HTMLInputElement).blur();
  }, 0);
};
